import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { LifeBuoy, Loader2, RefreshCw, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DepositRecovery — self-service recovery for stuck User Wallet deposits.
 *
 * If a user broadcast a STXOSHI transfer on-chain but closed the deposit dialog
 * before the app confirmed it, the funds are "stuck" (deducted on-chain, never
 * credited). This card auto-scans the user's recent on-chain transfers to the
 * hot wallet and credits any that were never recorded. It also accepts a manual
 * TXID for pinpoint recovery. All credits are idempotent (deduped by txid).
 */
export default function DepositRecovery({ onRecovered }) {
  const { isConnected, getAuthHeaders } = useWallet();
  const [scanning, setScanning] = useState(false);
  const [txid, setTxid] = useState("");
  const [lastResult, setLastResult] = useState(null);

  if (!isConnected) return null;

  async function runRecovery(withTxid) {
    setScanning(true);
    setLastResult(null);
    try {
      const body = withTxid && txid.trim() ? { txid: txid.trim() } : {};
      const r = await fetch(`${API}/api/flip-market/player-wallet/recover`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Recovery failed");

      if (d.pending) {
        toast.info(d.message || "Transaction still pending on-chain. Please wait and retry.");
        setScanning(false);
        return;
      }

      setLastResult(d);
      if (d.recovered_count > 0) {
        toast.success(d.message);
        setTxid("");
        onRecovered?.(d.new_balance);
      } else {
        toast.info(d.message);
      }
    } catch (e) {
      toast.error(e.message || "Recovery failed");
    } finally {
      setScanning(false);
    }
  }

  return (
    <Card className="glass-card border-cyan-500/40" data-testid="deposit-recovery-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <LifeBuoy className="w-4 h-4 text-cyan-400" />
          Recover Stuck Deposit
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Sent STXOSHI to your User Wallet but the balance never updated (e.g. you
          closed the dialog before it confirmed)? Tap the button below — we'll scan
          the blockchain and credit any confirmed deposits that were missed.
        </p>

        <Button
          onClick={() => runRecovery(false)}
          disabled={scanning}
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white"
          data-testid="recover-scan-btn"
        >
          {scanning ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking blockchain…
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" /> Check On-Chain &amp; Recover
            </>
          )}
        </Button>

        <div className="pt-1">
          <p className="text-xs text-muted-foreground mb-1.5">
            Have the transaction ID? Paste it for a pinpoint recovery:
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="0x… transaction ID"
              value={txid}
              onChange={(e) => setTxid(e.target.value)}
              className="font-mono text-xs"
              data-testid="recover-txid-input"
            />
            <Button
              variant="outline"
              onClick={() => runRecovery(true)}
              disabled={scanning || !txid.trim()}
              className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 flex-shrink-0"
              data-testid="recover-txid-btn"
            >
              Recover
            </Button>
          </div>
        </div>

        {lastResult && (
          <div
            className={`text-sm rounded-lg p-3 border ${
              lastResult.recovered_count > 0
                ? "border-green-500/40 bg-green-500/10 text-green-300"
                : "border-border bg-muted/30 text-muted-foreground"
            }`}
            data-testid="recover-result"
          >
            {lastResult.recovered_count > 0 && (
              <div className="flex items-center gap-2 font-semibold mb-1">
                <CheckCircle2 className="w-4 h-4" />
                Recovered {(lastResult.recovered_total_stxoshi / 1_000_000).toLocaleString()} STXOSHI
              </div>
            )}
            <div>{lastResult.message}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
